import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useLicense } from "../../licensing/LicenseContext";
import { getFolders, getTaxonomyNodes } from "../../api/adminApi";
import { uploadToWorkspace } from "../../api/workspaceApi";
import "./workspace.css";

/**
 * Puts a file into the archive and into one of my folders.
 *
 * The dialog exposes all three layers at once, because a document that arrives with its corporate
 * folder and its subject terms already set is the difference between an archive and a pile of files —
 * and asking later almost never happens. Both org fields are optional, and both are hidden entirely
 * when content-governance is not licensed, so an unlicensed deployment just sees "choose a file".
 */
export default function WorkspaceUploadModal({ open, folder, onClose, onUploaded }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { hasFeature, featuresResolved } = useLicense();
  const govEnabled = hasFeature && hasFeature("content-governance");
  // Render the org-folder/term pickers off govEnabled; only ASK for their contents once the licence
  // answer has landed, so an unlicensed site does not 403 every time the modal opens.
  const govFetchReady = govEnabled && featuresResolved;

  const [file, setFile] = useState(null);
  const [orgFolders, setOrgFolders] = useState([]);
  const [terms, setTerms] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [termId, setTermId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setFolderId("");
    setTermId("");
    setError("");
    setBusy(false);
  }, [open]);

  useEffect(() => {
    if (!open || !token || !govFetchReady) return;
    let alive = true;
    getFolders(token)
      .then((list) => alive && setOrgFolders((Array.isArray(list) ? list : []).filter((f) => f?.isActive)))
      .catch(() => alive && setOrgFolders([]));
    getTaxonomyNodes(token)
      .then((list) => alive && setTerms((Array.isArray(list) ? list : []).filter((n) => n?.isActive)))
      .catch(() => alive && setTerms([]));
    return () => { alive = false; };
  }, [open, token, govFetchReady]);

  const submit = useCallback(async () => {
    if (!file || !folder) return;
    setBusy(true);
    setError("");
    try {
      const res = await uploadToWorkspace(token, {
        file,
        personalFolderId: folder.id,
        folderId: folderId ? Number(folderId) : null,
        taxonomyNodeIds: termId ? [Number(termId)] : [],
      });
      onUploaded && onUploaded(res);
      onClose && onClose();
    } catch (e) {
      // The server's message is the useful one here — it names the actual obstacle (no grant on the
      // workspace base, a base still waiting on a DBA, cloud storage) rather than "upload failed".
      setError(e?.message || t("workspace.uploadFailed", { defaultValue: "The document could not be uploaded." }));
    } finally {
      setBusy(false);
    }
  }, [file, folder, folderId, termId, token, onClose, onUploaded, t]);

  if (!open) return null;

  const pick = (f) => { if (f) { setFile(f); setError(""); } };

  return (
    <div className="dbModalOverlay" onClick={busy ? undefined : onClose}>
      <div className="dbModalCard ws-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="dbModalTitle">
          {t("workspace.uploadTitle", { defaultValue: "Add a document" })}
        </div>
        <div className="ws-field__hint" style={{ marginBottom: 16 }}>
          {t("workspace.uploadInto", { defaultValue: "Into {{folder}}", folder: folder?.name || "" })}
        </div>

        {error && <div className="ws-error">{error}</div>}

        <div
          className={`ws-file${dragOver ? " ws-file--over" : ""}`}
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            pick(e.dataTransfer?.files?.[0]);
          }}
          style={{ marginBottom: 16 }}
        >
          <input
            ref={inputRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => pick(e.target.files?.[0])}
          />
          {file ? (
            <>
              <div className="ws-file__name">{file.name}</div>
              <div className="ws-file__size">{formatBytes(file.size)}</div>
            </>
          ) : (
            t("workspace.dropFile", { defaultValue: "Drop a file here, or click to choose one" })
          )}
        </div>

        {govEnabled && (
          <>
            <div className="ws-field">
              <label className="ws-field__label">
                {t("workspace.orgFolder", { defaultValue: "Company folder" })}
              </label>
              <select
                className="ws-field__select"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
              >
                <option value="">{t("workspace.noneOptional", { defaultValue: "None (optional)" })}</option>
                {orgFolders.map((f) => (
                  <option key={f.id} value={f.id}>{f.path || f.name}</option>
                ))}
              </select>
              <span className="ws-field__hint">
                {t("workspace.orgFolderHint", { defaultValue: "Where the company files this document." })}
              </span>
            </div>

            <div className="ws-field">
              <label className="ws-field__label">
                {t("workspace.term", { defaultValue: "Subject" })}
              </label>
              <select
                className="ws-field__select"
                value={termId}
                onChange={(e) => setTermId(e.target.value)}
              >
                <option value="">{t("workspace.noneOptional", { defaultValue: "None (optional)" })}</option>
                {terms.map((n) => (
                  <option key={n.id} value={n.id}>{n.path || n.name}</option>
                ))}
              </select>
              <span className="ws-field__hint">
                {t("workspace.termHint", { defaultValue: "What this document is about." })}
              </span>
            </div>
          </>
        )}

        <div className="ws-modal-actions">
          <button type="button" className="ws-btn" onClick={onClose} disabled={busy}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className="ws-btn ws-btn--primary"
            onClick={submit}
            disabled={busy || !file}
          >
            {busy
              ? t("workspace.uploading", { defaultValue: "Adding…" })
              : t("workspace.upload", { defaultValue: "Add document" })}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
